"use client"

import { useEffect, useState } from "react"
import dynamic from "next/dynamic"
import { useRouter } from "next/navigation"
import { useFirebaseAuth } from "@/lib/firebase-context"
import { useI18n } from "@/lib/i18n/context"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowRight, TrendingUp, Lightbulb, ShieldCheck, BarChart3, Users, Bot, LineChart } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { TechOrbitDisplay, Ripple } from "@/components/ui/modern-animated-sign-in"
import { TopStocksSection } from "./top-stocks-section"
import { ShapeLandingBackground } from "@/components/ui/shape-landing-hero"

function StockGraphSectionFallback() {
  return null
}

const StockGraphSection = dynamic(
  () =>
    import("./stock-graph-section")
      .then((m) => {
        const Comp = m?.StockGraphSection
        return { default: typeof Comp === "function" ? Comp : StockGraphSectionFallback }
      })
      .catch(() => ({ default: StockGraphSectionFallback })),
  { ssr: false },
)

interface OrbitIcon {
  component: () => React.ReactNode;
  className: string;
  duration?: number;
  delay?: number;
  radius?: number;
  path?: boolean;
  reverse?: boolean;
}

// Investment company logos orbiting around Igani Capital
// Same configuration as login/signup pages
const iconsArray: OrbitIcon[] = [
  // Rim 1 - Inner (3 logos, 120 degrees apart)
  {
    component: () => (
      <div className='w-12 h-12 rounded-full bg-white flex items-center justify-center border-2 border-green-400 shadow-lg shadow-green-500/50 p-1.5'>
        <Image
          src='/logos/blackrock.png'
          alt='BlackRock'
          width={48}
          height={48}
          className='rounded-full object-contain'
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '<span class="text-green-600 font-bold text-[10px]">BR</span>';
            }
          }}
        />
      </div>
    ),
    className: 'border-none bg-transparent',
    duration: 25,
    delay: 0,
    radius: 100,
    path: true,
    reverse: false,
  },
  {
    component: () => (
      <div className='w-12 h-12 rounded-full bg-white flex items-center justify-center border-2 border-green-400 shadow-lg shadow-green-500/50 p-1.5'>
        <Image
          src='/logos/vanguard.png'
          alt='Vanguard'
          width={48}
          height={48}
          className='rounded-full object-contain'
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '<span class="text-green-600 font-bold text-[10px]">V</span>';
            }
          }}
        />
      </div>
    ),
    className: 'border-none bg-transparent',
    duration: 25,
    delay: 8.33,
    radius: 100,
    path: true,
    reverse: false,
  },
  {
    component: () => (
      <div className='w-12 h-12 rounded-full bg-white flex items-center justify-center border-2 border-green-400 shadow-lg shadow-green-500/50 p-1.5'>
        <Image
          src='/logos/fidelity.png'
          alt='Fidelity'
          width={48}
          height={48}
          className='rounded-full object-contain'
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '<span class="text-green-600 font-bold text-[10px]">F</span>';
            }
          }}
        />
      </div>
    ),
    className: 'border-none bg-transparent',
    duration: 25,
    delay: 16.67,
    radius: 100,
    path: true,
    reverse: false,
  },
  // Rim 2 - Middle (3 logos, 120 degrees apart, counter-clockwise)
  {
    component: () => (
      <div className='w-14 h-14 rounded-full bg-white flex items-center justify-center border-2 border-green-400 shadow-lg shadow-green-500/50 p-1.5'>
        <Image
          src='/logos/charles-schwab.png'
          alt='Charles Schwab'
          width={56}
          height={56}
          className='rounded-full object-contain'
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '<span class="text-green-600 font-bold text-xs">CS</span>';
            }
          }}
        />
      </div>
    ),
    className: 'border-none bg-transparent',
    duration: 30,
    delay: 5,
    radius: 160,
    path: true,
    reverse: true,
  },
  {
    component: () => (
      <div className='w-14 h-14 rounded-full bg-white flex items-center justify-center border-2 border-green-400 shadow-lg shadow-green-500/50 p-1.5'>
        <Image
          src='/logos/morgan-stanley.png'
          alt='Morgan Stanley'
          width={56}
          height={56}
          className='rounded-full object-contain'
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '<span class="text-green-600 font-bold text-xs">MS</span>';
            }
          }}
        />
      </div>
    ),
    className: 'border-none bg-transparent',
    duration: 30,
    delay: 15,
    radius: 160,
    path: true,
    reverse: true,
  },
  {
    component: () => (
      <div className='w-14 h-14 rounded-full bg-white flex items-center justify-center border-2 border-green-400 shadow-lg shadow-green-500/50 p-1.5'>
        <Image
          src='/logos/goldman-sachs.png'
          alt='Goldman Sachs'
          width={56}
          height={56}
          className='rounded-full object-contain'
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '<span class="text-green-600 font-bold text-xs">GS</span>';
            }
          }}
        />
      </div>
    ),
    className: 'border-none bg-transparent',
    duration: 30,
    delay: 25,
    radius: 160,
    path: true,
    reverse: true,
  },
  // Rim 3 - Outer (2 logos, 180 degrees apart)
  {
    component: () => (
      <div className='w-14 h-14 rounded-full bg-white flex items-center justify-center border-2 border-green-400 shadow-lg shadow-green-500/50 p-1.5'>
        <Image
          src='/logos/jpmorgan.png'
          alt='JPMorgan'
          width={56}
          height={56}
          className='rounded-full object-contain'
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '<span class="text-green-600 font-bold text-xs">JPM</span>';
            }
          }}
        />
      </div>
    ),
    className: 'border-none bg-transparent',
    duration: 22,
    delay: 1.83,
    radius: 220,
    path: true,
    reverse: false,
  },
  {
    component: () => (
      <div className='w-14 h-14 rounded-full bg-white flex items-center justify-center border-2 border-green-400 shadow-lg shadow-green-500/50 p-1.5'>
        <Image
          src='/logos/wells-fargo.png'
          alt='Wells Fargo'
          width={56}
          height={56}
          className='rounded-full object-contain'
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = '<span class="text-green-600 font-bold text-xs">WF</span>';
            }
          }}
        />
      </div>
    ),
    className: 'border-none bg-transparent',
    duration: 22,
    delay: 12.83,
    radius: 220,
    path: true,
    reverse: false,
  },
];

export function IganiLanding({ showChart = true }: { showChart?: boolean }) {
  const router = useRouter()
  const { isAuthenticated, loading } = useFirebaseAuth()
  const { t } = useI18n()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    if (!loading && isAuthenticated) {
      router.push("/dashboard")
    }
  }, [isAuthenticated, loading, router])

  const handleSignIn = () => {
    router.push("/login")
  }

  if (!mounted || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground">{t("common.loading")}</div>
      </div>
    )
  }

  if (isAuthenticated) {
    return null // Will redirect
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header: single brand block, clear CTA, one h1 on page (hero) */}
      <header className="w-full border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-50">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14 md:h-16">
          <Link
            href="/"
            className="flex items-center gap-3 shrink-0 text-foreground no-underline outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md"
            aria-label="Igani Capital – Home"
          >
            <Image
              src="/igani-logo.png"
              alt=""
              width={36}
              height={36}
              className="rounded-full object-contain size-9 md:size-10"
              priority
            />
            <span className="text-base md:text-lg font-medium tracking-wide text-foreground uppercase">
              Igani Capital
            </span>
          </Link>
          <Button
            onClick={handleSignIn}
            variant="default"
            size="default"
            className="min-h-10 px-5 font-medium shadow-sm"
          >
            {t("landing.signIn")}
          </Button>
        </div>
      </header>

      <main className="flex-1 flex flex-col">
        {/* Hero Section with Orbit Display */}
        <section className="relative flex-1 flex items-center min-h-[85vh] bg-gradient-to-br from-background via-muted to-background dark:from-background dark:via-muted dark:to-background overflow-hidden">
          <Ripple mainCircleSize={80} />
          
          <div className="container mx-auto px-4 md:px-8 lg:px-12 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Side - Text Content */}
              <div className="relative z-30 flex flex-col justify-center space-y-8 lg:space-y-10 text-left">
                <div className="space-y-4">
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-[0.2em] text-foreground uppercase drop-shadow-2xl leading-tight">
                    Igani Capital
                  </h1>
                  <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-xl leading-relaxed">
                    {t("landing.tagline")}
                  </p>
                  <p className="text-sm md:text-base text-muted-foreground/80 max-w-lg leading-relaxed">
                    {t("landing.subTagline")}
                  </p>
                </div>
                
                <div className="flex flex-col gap-4 pt-4">
                  <Button
                    onClick={handleSignIn}
                    size="lg"
                    className="text-base md:text-lg px-8 md:px-10 py-6 md:py-7 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/50 hover:shadow-primary/70 transition-all duration-300 border-0 w-fit"
                  >
                    {t("landing.getStarted")} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    {t("landing.joinLeading")}
                  </p>
                </div>
              </div>

              {/* Right Side - Orbit Display */}
              <div className="relative z-10 w-full flex items-center justify-center lg:justify-end overflow-visible">
                <div className="w-full max-w-[500px] lg:max-w-[600px] aspect-square overflow-visible">
                  <TechOrbitDisplay iconsArray={iconsArray} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Top Stocks Section */}
        <TopStocksSection />

        {/* Stock Graphs & Analytics Section (lazy so "/" can use strict CSP without recharts/lodash eval) */}
        {showChart && <StockGraphSection />}

        {/* Features Section */}
        <section className="w-full py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-background border-t border-border">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-light tracking-[0.15em] text-foreground uppercase text-center mb-4 md:mb-6">
              Why Igani Capital
            </h2>
            <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-16 md:mb-20">
              One platform for stocks and crypto: track performance, join leagues, and make decisions with data and AI.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-12">
              <Card className="bg-card/50 backdrop-blur-sm border-border shadow-xl hover:shadow-2xl hover:border-primary/30 transition-all duration-300 h-full">
                <CardContent className="p-6 md:p-8 flex flex-col items-center text-center space-y-4 h-full">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
                    <TrendingUp className="h-7 w-7 md:h-8 md:w-8 text-primary" />
                  </div>
                  <h3 className="text-lg md:text-xl font-light tracking-wide text-foreground uppercase">Portfolio Tracking</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Monitor stocks and crypto with real-time data, P&L by period, and a single dashboard.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card/50 backdrop-blur-sm border-border shadow-xl hover:shadow-2xl hover:border-primary/30 transition-all duration-300 h-full">
                <CardContent className="p-6 md:p-8 flex flex-col items-center text-center space-y-4 h-full">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
                    <Lightbulb className="h-7 w-7 md:h-8 md:w-8 text-primary" />
                  </div>
                  <h3 className="text-lg md:text-xl font-light tracking-wide text-foreground uppercase">Analyst Insights</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Validated picks, transparent analysis, and an AI assistant for research and questions.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card/50 backdrop-blur-sm border-border shadow-xl hover:shadow-2xl hover:border-primary/30 transition-all duration-300 h-full">
                <CardContent className="p-6 md:p-8 flex flex-col items-center text-center space-y-4 h-full">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
                    <ShieldCheck className="h-7 w-7 md:h-8 md:w-8 text-primary" />
                  </div>
                  <h3 className="text-lg md:text-xl font-light tracking-wide text-foreground uppercase">Risk Analysis</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Risk and performance metrics, scenario simulations, and visualizations.
                  </p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-xl md:text-2xl font-light tracking-[0.1em] text-foreground uppercase text-center mt-20 mb-10">
              What We Offer
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <Card className="bg-card/40 backdrop-blur-sm border-border">
                <CardContent className="p-5 flex flex-col items-center text-center space-y-3">
                  <BarChart3 className="h-8 w-8 text-primary" />
                  <h4 className="text-sm font-medium uppercase tracking-wide text-foreground">Unified Portfolio</h4>
                  <p className="text-xs text-muted-foreground">Stocks, ETFs, and crypto in one view with real-time and historical performance.</p>
                </CardContent>
              </Card>
              <Card className="bg-card/40 backdrop-blur-sm border-border">
                <CardContent className="p-5 flex flex-col items-center text-center space-y-3">
                  <Users className="h-8 w-8 text-primary" />
                  <h4 className="text-sm font-medium uppercase tracking-wide text-foreground">Leagues</h4>
                  <p className="text-xs text-muted-foreground">Create or join leagues, compete on standings, and track rivals and seasons.</p>
                </CardContent>
              </Card>
              <Card className="bg-card/40 backdrop-blur-sm border-border">
                <CardContent className="p-5 flex flex-col items-center text-center space-y-3">
                  <Bot className="h-8 w-8 text-primary" />
                  <h4 className="text-sm font-medium uppercase tracking-wide text-foreground">AI Assistant</h4>
                  <p className="text-xs text-muted-foreground">Ask questions about markets, symbols, and portfolio context with natural language.</p>
                </CardContent>
              </Card>
              <Card className="bg-card/40 backdrop-blur-sm border-border">
                <CardContent className="p-5 flex flex-col items-center text-center space-y-3">
                  <LineChart className="h-8 w-8 text-primary" />
                  <h4 className="text-sm font-medium uppercase tracking-wide text-foreground">Analytics</h4>
                  <p className="text-xs text-muted-foreground">Ratings, validated picks, transparent analysis, and risk and performance tools.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      {/* Elegant Footer */}
      <footer className="w-full p-6 md:p-8 text-center text-xs text-muted-foreground border-t border-border bg-background/80 backdrop-blur-sm shrink-0">
        <p className="font-light tracking-wider uppercase">
          © {new Date().getFullYear()} Igani Capital. All rights reserved.
        </p>
      </footer>
    </div>
  )
}
